import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Trash2, Plus } from 'lucide-react';
import { SearchableProductDropdown } from './SearchableProductDropdown';

export interface FarmProduct {
  id: string;
  productName: string;
  harvestStartDate?: string;
  harvestEndDate?: string;
  estimatedYield?: string;
}

interface ProductSelectionFieldProps {
  products: FarmProduct[];
  onProductsChange: (products: FarmProduct[]) => void;
}

export const ProductSelectionField: React.FC<ProductSelectionFieldProps> = ({
  products,
  onProductsChange
}) => {
  const generateId = () => Math.random().toString(36).substr(2, 9);

  const addProduct = () => {
    const newProduct: FarmProduct = {
      id: generateId(),
      productName: '',
      harvestStartDate: '',
      harvestEndDate: '',
      estimatedYield: ''
    };
    onProductsChange([...products, newProduct]);
  };

  const removeProduct = (id: string) => {
    onProductsChange(products.filter(product => product.id !== id));
  };

  const updateProduct = (id: string, field: keyof FarmProduct, value: string) => {
    onProductsChange(
      products.map(product =>
        product.id === id ? { ...product, [field]: value } : product
      )
    );
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-base font-medium">Additional Products</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addProduct}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Product
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          Add products that have different harvest timing from the main harvest season above.
        </p>
      </div>

        {products.length === 0 && (
          <Card>
            <CardContent className="p-4 text-center text-muted-foreground">
              <p>No additional products added yet. Click "Add Product" if you have crops with different harvest timing.</p>
            </CardContent>
          </Card>
        )}

      {products.map((product, index) => (
        <Card key={product.id}>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Product {index + 1}</Label>
              {products.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeProduct(product.id)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor={`product-${product.id}`}>Product Name *</Label>
              <SearchableProductDropdown
                value={product.productName}
                onValueChange={(value) => updateProduct(product.id, 'productName', value)}
                placeholder="Search and select product"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`harvest-start-${product.id}`}>Harvest Start Date</Label>
                <Input
                  id={`harvest-start-${product.id}`}
                  type="date"
                  value={product.harvestStartDate || ''}
                  onChange={(e) => updateProduct(product.id, 'harvestStartDate', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor={`harvest-end-${product.id}`}>Harvest End Date</Label>
                <Input
                  id={`harvest-end-${product.id}`}
                  type="date"
                  value={product.harvestEndDate || ''}
                  onChange={(e) => updateProduct(product.id, 'harvestEndDate', e.target.value)}
                  min={product.harvestStartDate || undefined}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`yield-${product.id}`}>Estimated Yield (kg)</Label>
              <Input
                id={`yield-${product.id}`}
                type="number"
                step="0.1"
                value={product.estimatedYield || ''}
                onChange={(e) => updateProduct(product.id, 'estimatedYield', e.target.value)}
                placeholder="e.g., 500"
              />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};